<template>
  <div class="wcv">
    <div class="wcv-bar">
      <b-input-group size="sm" class="wcv-search">
        <b-input-group-prepend is-text><i class="fas fa-search"/></b-input-group-prepend>
        <b-form-input v-model="search" :placeholder="$t('whatsappHub.chats.search')" debounce="150"/>
      </b-input-group>
      <div class="wcv-fchips">
        <button type="button" class="wcv-fchip wcv-fchip--purple" :class="{'wcv-fchip--on': unreadOnly}" :aria-pressed="unreadOnly" @click="unreadOnly = !unreadOnly">
          <i :class="unreadOnly ? 'fas fa-circle-check' : 'far fa-circle'"/>{{ $t('whatsappHub.chats.unreadOnly') }}
        </button>
        <button type="button" class="wcv-fchip wcv-fchip--green" :class="{'wcv-fchip--on': sessionOnly}" :aria-pressed="sessionOnly" @click="sessionOnly = !sessionOnly">
          <i :class="sessionOnly ? 'fas fa-circle-check' : 'far fa-circle'"/>{{ $t('whatsappHub.filters.sessionOnly') }}
        </button>
      </div>
      <span class="wcv-bar__spacer"/>
      <span class="wcv-count"><b class="tabular">{{ visible.length }}</b> / {{ rows.length }}</span>
      <button type="button" class="wcv-tool" v-b-tooltip.hover :title="$t('whatsappHub.reload')" @click="refresh">
        <i class="fas fa-redo" :class="{'fa-spin': loading}"/>
      </button>
      <b-button variant="success" size="sm" class="wcv-new" @click="openNew">
        <i class="fas fa-plus mr-25"/>{{ $t('whatsappHub.newChat.button') }}
      </b-button>
    </div>

    <!-- new conversation: find any guest of the branch -->
    <b-modal v-model="newDialog" centered hide-footer size="md" body-class="p-0" @shown="focusSearch">
      <template #modal-title>
        <span class="wcv-new__title"><i class="fab fa-whatsapp"/>{{ $t('whatsappHub.newChat.title') }}</span>
      </template>
      <div class="wcv-new__body">
        <b-input-group>
          <b-input-group-prepend is-text><i class="fas fa-search"/></b-input-group-prepend>
          <b-form-input ref="newSearch" v-model="newQuery" :placeholder="$t('whatsappHub.newChat.placeholder')" debounce="250" autocomplete="off"/>
        </b-input-group>
        <small class="wcv-new__hint">{{ $t('whatsappHub.newChat.hint') }}</small>

        <div v-if="searching" class="wcv-new__state"><b-spinner small class="mr-50"/>{{ $t('Loading') }}…</div>
        <div v-else-if="newQuery.trim().length < 2" class="wcv-new__state">{{ $t('whatsappHub.newChat.typeMore') }}</div>
        <div v-else-if="found.length === 0" class="wcv-new__state">{{ $t('whatsappHub.newChat.none') }}</div>
        <div v-else class="wcv-new__list">
          <button v-for="c in found" :key="c.clientId" type="button" class="wcv-new__row" :disabled="!c.phone" @click="startChat(c)">
            <span class="wcv-avatar wcv-avatar--sm" :style="avatarStyle(c.name)">{{ initials(c.name) }}</span>
            <span class="wcv-new__main">
              <span class="wcv-new__name">{{ c.name || ('#' + c.clientId) }}</span>
              <span class="wcv-new__meta">
                <span v-if="c.phone" class="tabular">{{ c.phone }}</span>
                <span v-else class="wcv-new__nophone"><i class="fas fa-phone-slash mr-25"/>{{ $t('whatsappHub.table.noPhone') }}</span>
                <span v-if="c.email">· {{ c.email }}</span>
              </span>
            </span>
            <span class="wcv-new__side">
              <span v-if="c.messageCount" class="wcv-new__count"><i class="far fa-comment mr-25"/>{{ c.messageCount }}</span>
              <span class="wcv-session" :class="c.sessionOpen ? 'wcv-session--open' : ''"><i/>{{ c.sessionOpen ? $t('whatsappHub.table.sessionOpenShort') : $t('whatsappHub.table.templateRequired') }}</span>
            </span>
          </button>
        </div>
      </div>
    </b-modal>

    <div v-if="loading && rows.length === 0" class="wcv-empty"><b-spinner small/></div>
    <div v-else-if="visible.length === 0" class="wcv-empty">
      <i class="fab fa-whatsapp"/>
      <p>{{ $t('whatsappHub.empty.chats') }}</p>
    </div>
    <div v-else class="wcv-list">
      <div v-for="row in visible" :key="row.id" class="wcv-row" :class="{'wcv-row--unread': row.unseen}" role="button" @click="$emit('open-chat', row)">
        <span class="wcv-avatar" :style="avatarStyle(row.customerName)">{{ initials(row.customerName) }}<i v-if="row.unseen" class="wcv-avatar__dot"/></span>
        <div class="wcv-main">
          <div class="wcv-top">
            <span class="wcv-name">{{ row.customerName }}</span>
            <span v-if="row.phone" class="wcv-phone tabular">{{ row.phone }}</span>
            <span class="wcv-session" :class="row.sessionOpen ? 'wcv-session--open' : ''"><i/>{{ row.sessionOpen ? $t('whatsappHub.table.sessionOpenShort') : $t('whatsappHub.table.templateRequired') }}</span>
          </div>
          <div v-if="row.contractAddress" class="wcv-address"><i class="fas fa-home mr-25"/>{{ row.contractAddress }}</div>
          <p v-if="row.lastMessage" class="wcv-preview">
            <i :class="row.lastMessage.isIncoming ? 'fas fa-arrow-down wcv-dir wcv-dir--in' : 'fas fa-arrow-up wcv-dir'"/>
            <i v-if="!row.lastMessage.isIncoming" :class="statusMeta(row.lastMessage.status).icon" class="wcv-tick" :data-tone="statusMeta(row.lastMessage.status).tone"/>
            {{ preview(row.lastMessage) }}
          </p>
        </div>
        <div class="wcv-side">
          <span class="wcv-ago">{{ ago(row.lastDate) }}</span>
          <span class="wcv-n tabular">{{ row.messageCount }} <i class="far fa-comment"/></span>
          <div class="wcv-actions" @click.stop>
            <button v-if="row.contractId" type="button" class="wcv-action" v-b-tooltip.hover :title="$t('whatsappHub.actions.reservation')" @click="$emit('open-contract', row.contractId)">
              <i class="fa-solid fa-file-contract"/>
            </button>
            <button type="button" class="wcv-action wcv-action--send" :disabled="!row.phone" v-b-tooltip.hover :title="$t('whatsappHub.actions.sendTemplate')"
                    @click="$emit('compose', [row], {mode: 'TEMPLATE'})">
              <i class="fas fa-paper-plane"/>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions} from 'vuex'
import {avatarStyle, initials, statusMeta} from './whatsappUtils'

/** Every WhatsApp conversation of the branch, newest first — the inbox. */
export default {
  name: 'WhatsappConversations',
  props: {
    buid: {type: [Number, String], default: null},
  },
  data: () => ({
    rows: [],
    loading: false,
    search: '',
    unreadOnly: false,
    sessionOnly: false,
    newDialog: false,
    newQuery: '',
    found: [],
    searching: false,
    searchSeq: 0,
  }),
  watch: {
    newQuery() {
      this.searchClients();
    },
  },
  computed: {
    visible() {
      const q = (this.search || '').trim().toLowerCase();
      return this.rows.filter(r => {
        if (this.unreadOnly && !r.unseen) return false;
        if (this.sessionOnly && !r.sessionOpen) return false;
        if (q === '') return true;
        return [r.customerName, r.phone, r.contractAddress, r.lastMessage && r.lastMessage.body].filter(Boolean).join(' ').toLowerCase().includes(q);
      });
    },
  },
  created() {
    this.refresh();
    this.$watch('buid', () => this.refresh());
  },
  methods: {
    ...mapActions('whatsapp', ['hubConversations', 'hubSearchClients']),
    initials, avatarStyle, statusMeta,
    openNew() {
      this.newQuery = '';
      this.found = [];
      this.newDialog = true;
    },
    focusSearch() {
      const el = this.$refs.newSearch && this.$refs.newSearch.$el;
      if (el) el.focus();
    },
    searchClients() {
      const q = this.newQuery.trim();
      if (q.length < 2 || !this.buid) { this.found = []; return; }
      const seq = ++this.searchSeq;
      this.searching = true;
      this.hubSearchClients({buid: this.buid, q}).then(list => {
        if (seq !== this.searchSeq) return;
        this.found = list;
      }).catch(error => {
        this.$store.dispatch('notifications/error', {title: {text: 'WhatsApp'}, message: error});
      }).finally(() => { if (seq === this.searchSeq) this.searching = false; });
    },
    startChat(c) {
      this.newDialog = false;
      this.$emit('open-chat', {clientId: c.clientId, name: c.name, phone: c.phone, contractId: c.contractId, sessionOpen: c.sessionOpen});
    },
    refresh() {
      if (!this.buid) return;
      this.loading = true;
      this.hubConversations({buids: [this.buid]}).then(ans => {
        this.rows = (ans.rows || []).map(r => ({...r, clientId: r.clientId, name: r.customerName}));
        this.$emit('loaded', {rows: this.rows, unread: ans.unread || 0});
      }).catch(error => {
        this.$store.dispatch('notifications/error', {title: {text: 'WhatsApp'}, message: error});
      }).finally(() => this.loading = false);
    },
    markSeenLocally(clientId) {
      const row = this.rows.find(r => r.clientId === clientId);
      if (row && row.unseen) {
        row.unseen = false;
        this.$emit('loaded', {rows: this.rows, unread: this.rows.filter(r => r.unseen).length});
      }
    },
    ago(date) {
      const m = this.$moment(date);
      if (!m.isValid()) return '';
      return m.isSame(this.$moment(), 'day') ? m.format('HH:mm') : m.fromNow();
    },
    preview(message) {
      if (!message) return '';
      if (message.type === 'MEDIA' && !message.body) return '📎 ' + this.$t('whatsappHub.thread.media');
      const text = String(message.body || '').replace(/<[^>]+>/g, '');
      return text.length > 120 ? text.substring(0, 120) + '…' : text;
    },
  }
}
</script>

<style lang="scss" scoped>
.tabular { font-variant-numeric: tabular-nums; }
.wcv-bar { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; margin-bottom: 10px; }
.wcv-bar__spacer { flex: 1; }
.wcv-search { width: 240px; }
.wcv-fchips { display: inline-flex; gap: 6px; }
.wcv-fchip {
  display: inline-flex; align-items: center; gap: 7px; height: 32px; padding: 0 12px; cursor: pointer;
  border: 1px dashed rgba(15, 23, 42, .18); border-radius: 999px; background: transparent;
  font-size: .72rem; font-weight: 700; color: #64748b; white-space: nowrap; transition: all .15s;
  i { font-size: .8rem; color: #cbd5e1; }
  &:hover { border-color: rgba(15, 23, 42, .35); color: #1f2a44; }
  &:focus { outline: none; }
  &--on { border-style: solid; }
  &--green.wcv-fchip--on { background: rgba(37, 211, 102, .14); border-color: rgba(37, 211, 102, .6); color: #1f9d57; i { color: #1f9d57; } }
  &--purple.wcv-fchip--on { background: rgba(115, 103, 240, .12); border-color: rgba(115, 103, 240, .6); color: #7367f0; i { color: #7367f0; } }
}
.wcv-count { font-size: .76rem; color: #64748b; }
.wcv-tool {
  width: 30px; height: 30px; border: none; border-radius: 9px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; background: rgba(15, 23, 42, .05); color: #64748b; font-size: .74rem;
  &:focus { outline: none; }
}
.wcv-empty { text-align: center; padding: 48px 0 30px; color: #94a3b8; i { font-size: 2.4rem; opacity: .35; } p { margin-top: 8px; font-size: .86rem; } }
.wcv-new { white-space: nowrap; }
.wcv-new__title { display: inline-flex; align-items: center; gap: 8px; font-weight: 800; i { color: #25d366; font-size: 1.1rem; } }
.wcv-new__body { padding: 16px 18px 18px; display: flex; flex-direction: column; gap: 8px; }
.wcv-new__hint { font-size: .7rem; color: #94a3b8; }
.wcv-new__state { text-align: center; padding: 26px 0 10px; color: #94a3b8; font-size: .8rem; }
.wcv-new__list { display: flex; flex-direction: column; max-height: 50vh; overflow-y: auto; margin-top: 4px; }
.wcv-new__row {
  display: flex; align-items: center; gap: 10px; width: 100%; text-align: left; cursor: pointer;
  border: none; border-bottom: 1px solid rgba(15, 23, 42, .06); background: transparent; padding: 9px 6px; border-radius: 10px; transition: background .15s;
  &:hover { background: rgba(37, 211, 102, .08); }
  &:focus { outline: none; }
  &:disabled { opacity: .55; cursor: not-allowed; }
  &:last-child { border-bottom: none; }
}
.wcv-avatar--sm { width: 34px; height: 34px; font-size: .7rem; }
.wcv-new__main { flex: 1; min-width: 0; display: flex; flex-direction: column; }
.wcv-new__name { font-size: .84rem; font-weight: 700; color: #1f2a44; text-transform: capitalize; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wcv-new__meta { font-size: .7rem; color: #94a3b8; display: flex; gap: 6px; flex-wrap: wrap; }
.wcv-new__nophone { color: #ea5455; font-weight: 700; }
.wcv-new__side { display: flex; flex-direction: column; align-items: flex-end; gap: 4px; flex-shrink: 0; }
.wcv-new__count { font-size: .68rem; color: #64748b; }
.wcv-list { display: flex; flex-direction: column; }
.wcv-row {
  display: flex; align-items: flex-start; gap: 12px; padding: 12px 10px; border-radius: 12px; cursor: pointer;
  border-bottom: 1px solid rgba(15, 23, 42, .05); transition: background .15s;
  &:last-child { border-bottom: none; }
  &:hover { background: rgba(37, 211, 102, .05); }
  &--unread .wcv-name { font-weight: 800; }
  &--unread .wcv-preview { font-weight: 600; color: #1f2a44; }
}
.wcv-avatar { position: relative; width: 40px; height: 40px; border-radius: 50%; flex-shrink: 0; display: inline-flex; align-items: center; justify-content: center; font-size: .78rem; font-weight: 800; color: #fff; }
.wcv-avatar__dot { position: absolute; right: -1px; top: -1px; width: 10px; height: 10px; border-radius: 50%; background: #7367f0; border: 2px solid #fff; }
.wcv-main { flex: 1; min-width: 0; }
.wcv-top { display: flex; align-items: center; flex-wrap: wrap; gap: 8px; }
.wcv-name { color: #1f2a44; font-size: .88rem; font-weight: 700; text-transform: capitalize; }
.wcv-phone { font-size: .72rem; color: #94a3b8; }
.wcv-session {
  display: inline-flex; align-items: center; gap: 5px; font-size: .6rem; font-weight: 800; text-transform: uppercase; letter-spacing: .04em;
  padding: 2px 8px; border-radius: 999px; background: rgba(255, 159, 67, .14); color: #c77400;
  i { width: 5px; height: 5px; border-radius: 50%; background: #ff9f43; }
  &--open { background: rgba(37, 211, 102, .14); color: #1f9d57; i { background: #25d366; } }
}
.wcv-address { font-size: .7rem; color: #94a3b8; margin-top: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.wcv-preview { margin: 4px 0 0; color: #475569; font-size: .8rem; line-height: 1.35; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
.wcv-dir { font-size: .6rem; color: #94a3b8; margin-right: 3px; &--in { color: #1f9d57; } }
.wcv-tick { font-size: .66rem; margin-right: 4px; &[data-tone="ok"] { color: #94a3b8; } &[data-tone="read"] { color: #34b7f1; } &[data-tone="bad"] { color: #ea5455; } &[data-tone="muted"] { color: #cbd5e1; } }
.wcv-side { display: flex; flex-direction: column; align-items: flex-end; gap: 2px; flex-shrink: 0; }
.wcv-ago { font-size: .74rem; font-weight: 800; color: #1f9d57; white-space: nowrap; }
.wcv-n { font-size: .66rem; color: #94a3b8; }
.wcv-actions { display: inline-flex; gap: 6px; margin-top: 4px; opacity: 0; transition: opacity .15s; }
.wcv-row:hover .wcv-actions { opacity: 1; }
@media (hover: none) { .wcv-actions { opacity: 1; } }
.wcv-action {
  width: 28px; height: 28px; border: 1px solid rgba(15, 23, 42, .08); border-radius: 9px; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center; background: #fff; color: #64748b; font-size: .72rem; transition: all .15s;
  &:hover { border-color: #7367f0; color: #7367f0; }
  &:focus { outline: none; }
  &:disabled { opacity: .4; }
  &--send:hover { border-color: #28c76f; color: #1f9d57; }
}
</style>

<style lang="scss">
.dark-layout {
  .wcv-fchip { border-color: rgba(255, 255, 255, .2); color: #94a3b8; i { color: #4b5261; } &:hover { color: #e9ecf2; border-color: rgba(255, 255, 255, .4); } }
  .wcv-fchip--green.wcv-fchip--on { color: #4ade80; i { color: #4ade80; } }
  .wcv-fchip--purple.wcv-fchip--on { color: #a89cff; i { color: #a89cff; } }
  .wcv-count { color: #94a3b8; }
  .wcv-new__row { border-color: rgba(255, 255, 255, .06); &:hover { background: rgba(37, 211, 102, .1); } }
  .wcv-new__name { color: #e9ecf2; }
  .wcv-tool { background: rgba(255, 255, 255, .06); color: #94a3b8; }
  .wcv-row { border-color: rgba(255, 255, 255, .06); &:hover { background: rgba(37, 211, 102, .08); } }
  .wcv-row--unread .wcv-preview { color: #e9ecf2; }
  .wcv-name { color: #e9ecf2; }
  .wcv-preview { color: #b4b7bd; }
  .wcv-avatar__dot { border-color: #283046; }
  .wcv-action { background: #283046; border-color: rgba(255, 255, 255, .12); color: #94a3b8; }
}
</style>
